import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { billingApi, membersApi } from '../services/api';
import { BillingInterval, MembershipPlan, MemberSubscription, PlanInput } from '../types';

type ApiError = { response?: { data?: { error?: string } } };
const errorText = (err: unknown, fallback: string) => (err as ApiError)?.response?.data?.error || fallback;

const formatMoney = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

const perkList = (p: Pick<MembershipPlan, 'includesLibrary' | 'includesFeedback' | 'includesLive'>) =>
  [p.includesLibrary && 'Members library', p.includesFeedback && 'Video feedback', p.includesLive && 'Live classes']
    .filter(Boolean)
    .join(' · ') || '—';

interface PlanFormValues {
  name: string;
  description: string;
  price: string;
  interval: BillingInterval;
  includesLibrary: boolean;
  includesFeedback: boolean;
  includesLive: boolean;
  isActive: boolean;
}

const emptyPlan: PlanFormValues = {
  name: '',
  description: '',
  price: '',
  interval: 'month',
  includesLibrary: true,
  includesFeedback: false,
  includesLive: false,
  isActive: true,
};

interface GrantFormValues {
  userId: string;
  planId: string;
  expiresAt: string;
  note: string;
}

const BillingManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingPlan, setEditingPlan] = useState<MembershipPlan | 'new' | null>(null);
  const [showGrantForm, setShowGrantForm] = useState(false);

  const { data: overview, isLoading, error } = useQuery({
    queryKey: ['billing'],
    queryFn: billingApi.getOverview,
  });
  const { data: subscriptions } = useQuery({
    queryKey: ['billing', 'subscriptions'],
    queryFn: billingApi.getSubscriptions,
  });
  const { data: members } = useQuery({
    queryKey: ['members'],
    queryFn: membersApi.getAll,
    enabled: showGrantForm,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['billing'] });

  const connectMutation = useMutation({
    mutationFn: billingApi.connectStripe,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
  const refreshMutation = useMutation({ mutationFn: billingApi.refreshStripe, onSuccess: invalidate });
  const toggleMutation = useMutation({ mutationFn: billingApi.setMembershipsEnabled, onSuccess: invalidate });

  // Back from Stripe's onboarding: re-read the account status once
  const stripeReturn = searchParams.get('stripe');
  useEffect(() => {
    if (stripeReturn === 'return' || stripeReturn === 'refresh') {
      refreshMutation.mutate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeReturn]);

  // ---- Plan form
  const planForm = useForm<PlanFormValues>({ defaultValues: emptyPlan });
  const savePlanMutation = useMutation({
    mutationFn: (values: PlanFormValues) => {
      const data: PlanInput = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        priceCents: Math.round(Number(values.price) * 100),
        interval: values.interval,
        includesLibrary: values.includesLibrary,
        includesFeedback: values.includesFeedback,
        includesLive: values.includesLive,
        isActive: values.isActive,
      };
      return editingPlan && editingPlan !== 'new'
        ? billingApi.updatePlan(editingPlan.id, data)
        : billingApi.createPlan(data);
    },
    onSuccess: () => {
      invalidate();
      setEditingPlan(null);
    },
  });

  const openPlanForm = (plan: MembershipPlan | 'new') => {
    savePlanMutation.reset();
    planForm.reset(
      plan === 'new'
        ? emptyPlan
        : {
            name: plan.name,
            description: plan.description ?? '',
            price: (plan.priceCents / 100).toFixed(2),
            interval: plan.interval,
            includesLibrary: plan.includesLibrary,
            includesFeedback: plan.includesFeedback,
            includesLive: plan.includesLive,
            isActive: plan.isActive,
          },
    );
    setEditingPlan(plan);
  };

  // ---- Grant form
  const grantForm = useForm<GrantFormValues>({ defaultValues: { userId: '', planId: '', expiresAt: '', note: '' } });
  const grantMutation = useMutation({
    mutationFn: (v: GrantFormValues) =>
      billingApi.grant({
        userId: Number(v.userId),
        planId: Number(v.planId),
        expiresAt: v.expiresAt ? new Date(`${v.expiresAt}T23:59:59`).toISOString() : undefined,
        note: v.note.trim() || undefined,
      }),
    onSuccess: () => {
      invalidate();
      grantForm.reset();
      setShowGrantForm(false);
    },
  });
  const revokeMutation = useMutation({ mutationFn: billingApi.revokeGrant, onSuccess: invalidate });

  const handleRevoke = (sub: MemberSubscription) => {
    if (window.confirm(`Remove ${sub.user.name || sub.user.email}'s free access to ${sub.plan.name}?`)) {
      revokeMutation.mutate(sub.id);
    }
  };

  const handleToggle = () => {
    if (!overview) return;
    const next = !overview.membershipsEnabled;
    const message = next
      ? 'Turn on memberships? Students will need a plan to watch content marked "Members only", send videos for feedback, or join live classes. Owners and instructors keep full access.'
      : 'Turn off memberships? Every member will see all content again, whatever their plan.';
    if (window.confirm(message)) toggleMutation.mutate(next);
  };

  if (isLoading) return <div className="loading">Loading billing…</div>;
  if (error || !overview) return <div className="error-text">Failed to load billing settings.</div>;

  const { stripe, currency } = overview;
  const activePlans = overview.plans.filter((p) => p.isActive);

  return (
    <div className="billing-management">
      <div className="page-header">
        <h1>Memberships &amp; Billing</h1>
      </div>

      <p className="admin-page-intro">
        Sell membership plans to students anywhere. Payments go straight to your studio’s own Stripe
        account. Mark moves and patterns as <strong>Members only</strong> in their edit forms; everything
        else stays free for signed-in students.
      </p>

      {!overview.stripeConfigured && (
        <div className="error-text">Payments aren’t configured on this server yet.</div>
      )}

      <div className="billing-cards">
        <section className="form-container billing-card">
          <h2>Stripe payouts</h2>
          {!stripe.connected ? (
            <>
              <p>Connect a Stripe account so students can pay you. It takes about five minutes.</p>
              <button
                className="btn btn-primary"
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending || !overview.stripeConfigured}
              >
                {connectMutation.isPending ? 'Opening Stripe…' : 'Connect Stripe'}
              </button>
            </>
          ) : stripe.ready ? (
            <>
              <p className="billing-status billing-status--ok">✓ Ready to take payments</p>
              <p>Payouts, refunds and disputes are handled in your Stripe Dashboard.</p>
              <a className="btn btn-secondary" href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">
                Open Stripe Dashboard
              </a>
            </>
          ) : (
            <>
              <p className="billing-status billing-status--warn">Stripe setup isn’t finished</p>
              <p>Stripe needs a few more details before you can take payments.</p>
              <div className="form-actions">
                <button className="btn btn-primary" onClick={() => connectMutation.mutate()} disabled={connectMutation.isPending}>
                  {connectMutation.isPending ? 'Opening Stripe…' : 'Finish Stripe setup'}
                </button>
                <button className="btn btn-secondary" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}>
                  {refreshMutation.isPending ? 'Checking…' : 'Check status'}
                </button>
              </div>
            </>
          )}
          {(connectMutation.error || refreshMutation.error) && (
            <div className="error-text">{errorText(connectMutation.error || refreshMutation.error, 'Could not reach Stripe')}</div>
          )}
        </section>

        <section className="form-container billing-card">
          <h2>Memberships</h2>
          <p className={`billing-status ${overview.membershipsEnabled ? 'billing-status--ok' : ''}`}>
            {overview.membershipsEnabled ? '✓ On: students need a plan for members-only content' : 'Off: every member sees everything'}
          </p>
          {!overview.membershipsEnabled && activePlans.length === 0 && (
            <p>Create at least one plan below before turning memberships on.</p>
          )}
          <button
            className={`btn ${overview.membershipsEnabled ? 'btn-secondary' : 'btn-primary'}`}
            onClick={handleToggle}
            disabled={toggleMutation.isPending || (!overview.membershipsEnabled && activePlans.length === 0)}
          >
            {overview.membershipsEnabled ? 'Turn off memberships' : 'Turn on memberships'}
          </button>
        </section>

        <section className="form-container billing-card">
          <h2>Your LearnSalsa plan</h2>
          <dl className="billing-fees">
            <dt>Platform fee on member payments</dt>
            <dd>{overview.platformFeePercent}%</dd>
            <dt>Monthly subscription</dt>
            <dd>{overview.platformMonthlyFeeCents != null ? `${formatMoney(overview.platformMonthlyFeeCents, 'usd')} / month (invoiced)` : 'None'}</dd>
          </dl>
          <p className="billing-muted">Stripe’s own processing fees are charged to your Stripe account. Questions about your plan? Contact LearnSalsa.</p>
        </section>
      </div>

      {/* ---- Plans ---- */}
      <div className="page-header billing-section-header">
        <h2>Plans</h2>
        <button className="btn btn-primary" onClick={() => openPlanForm('new')} disabled={editingPlan !== null}>
          Add plan
        </button>
      </div>

      {editingPlan && (
        <div className="form-container">
          <h2>{editingPlan === 'new' ? 'New plan' : `Edit ${editingPlan.name}`}</h2>
          <form onSubmit={planForm.handleSubmit((v) => savePlanMutation.mutate(v))} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="plan-name">Name *</label>
                <input
                  id="plan-name"
                  placeholder="e.g. Standard"
                  className={planForm.formState.errors.name ? 'error' : ''}
                  {...planForm.register('name', { required: 'Name is required', maxLength: { value: 80, message: 'Up to 80 characters' } })}
                />
                {planForm.formState.errors.name && <span className="error-text">{planForm.formState.errors.name.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="plan-price">Price ({currency.toUpperCase()}) *</label>
                <input
                  id="plan-price"
                  type="number"
                  step="0.01"
                  min="1"
                  placeholder="39.00"
                  className={planForm.formState.errors.price ? 'error' : ''}
                  {...planForm.register('price', {
                    required: 'Price is required',
                    validate: (v) => (Number(v) >= 1 && Number(v) <= 10000) || 'Between 1 and 10,000',
                  })}
                />
                {planForm.formState.errors.price && <span className="error-text">{planForm.formState.errors.price.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="plan-interval">Billed</label>
                <select id="plan-interval" {...planForm.register('interval')}>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
            </div>
            {editingPlan !== 'new' && (
              <p className="billing-muted">
                Changing the price only affects new members. Current members keep the price they signed up at.
              </p>
            )}
            <div className="form-group">
              <label htmlFor="plan-description">Description</label>
              <textarea id="plan-description" rows={2} placeholder="What members get" {...planForm.register('description')} />
            </div>
            <fieldset className="form-group billing-perks">
              <legend>Includes</legend>
              <label><input type="checkbox" {...planForm.register('includesLibrary')} /> Members-only moves and patterns</label>
              <label><input type="checkbox" {...planForm.register('includesFeedback')} /> Send videos for instructor feedback</label>
              <label><input type="checkbox" {...planForm.register('includesLive')} /> Live classes and replays</label>
            </fieldset>
            {editingPlan !== 'new' && (
              <div className="form-group">
                <label><input type="checkbox" {...planForm.register('isActive')} /> Offer this plan to new members</label>
              </div>
            )}
            {savePlanMutation.error && (
              <div className="error-text">{errorText(savePlanMutation.error, 'Failed to save plan')}</div>
            )}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={savePlanMutation.isPending}>
                {savePlanMutation.isPending ? 'Saving…' : 'Save plan'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditingPlan(null)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {overview.plans.length > 0 ? (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Price</th>
                <th>Includes</th>
                <th>Members</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overview.plans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <strong>{plan.name}</strong>
                    {plan.description && <div className="billing-muted">{plan.description}</div>}
                  </td>
                  <td>{formatMoney(plan.priceCents, currency)} / {plan.interval}</td>
                  <td>{perkList(plan)}</td>
                  <td>{plan.activeMembers}</td>
                  <td>
                    {!plan.isActive ? 'Hidden' : stripe.connected && !plan.inStripe ? 'Not synced to Stripe' : 'Offered'}
                  </td>
                  <td>
                    <button className="btn btn-small btn-secondary" onClick={() => openPlanForm(plan)} disabled={editingPlan !== null}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No plans yet. Most studios start with two or three, e.g. $39, $79 and $129 a month.</p>
        </div>
      )}

      {/* ---- Members with access ---- */}
      <div className="page-header billing-section-header">
        <h2>Members with access</h2>
        <button className="btn btn-primary" onClick={() => setShowGrantForm(true)} disabled={showGrantForm || overview.plans.length === 0}>
          Give free access
        </button>
      </div>
      <p className="admin-page-intro">
        Give a plan for free to students who already pay you in person, or for a trial. Paid members appear here
        automatically.
      </p>

      {showGrantForm && (
        <div className="form-container">
          <h2>Give free access</h2>
          <form onSubmit={grantForm.handleSubmit((v) => grantMutation.mutate(v))} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="grant-user">Member *</label>
                <select id="grant-user" {...grantForm.register('userId', { required: 'Choose a member' })}>
                  <option value="">{members ? 'Choose…' : 'Loading…'}</option>
                  {members?.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name || m.email} ({m.email})
                    </option>
                  ))}
                </select>
                {grantForm.formState.errors.userId && <span className="error-text">{grantForm.formState.errors.userId.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="grant-plan">Plan *</label>
                <select id="grant-plan" {...grantForm.register('planId', { required: 'Choose a plan' })}>
                  <option value="">Choose…</option>
                  {overview.plans.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {grantForm.formState.errors.planId && <span className="error-text">{grantForm.formState.errors.planId.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="grant-ends">Ends (optional)</label>
                <input id="grant-ends" type="date" {...grantForm.register('expiresAt')} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="grant-note">Note</label>
              <input id="grant-note" placeholder="e.g. Thursday in-person class" {...grantForm.register('note')} />
            </div>
            {grantMutation.error && <div className="error-text">{errorText(grantMutation.error, 'Failed to give access')}</div>}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={grantMutation.isPending}>
                {grantMutation.isPending ? 'Saving…' : 'Give access'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowGrantForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {subscriptions && subscriptions.length > 0 ? (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Plan</th>
                <th>Type</th>
                <th>Renews / ends</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td>
                    {sub.user.name || sub.user.email}
                    <div className="billing-muted">{sub.user.email}</div>
                  </td>
                  <td>{sub.plan.name}</td>
                  <td>
                    {sub.source === 'manual' ? 'Free access' : 'Paid'}
                    {sub.status === 'past_due' && <div className="error-text">Payment failed, retrying</div>}
                    {sub.note && <div className="billing-muted">{sub.note}</div>}
                  </td>
                  <td>
                    {sub.source === 'manual'
                      ? sub.expiresAt ? `Ends ${new Date(sub.expiresAt).toLocaleDateString()}` : 'No end date'
                      : sub.currentPeriodEnd
                        ? `${sub.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`
                        : '—'}
                  </td>
                  <td>
                    {sub.source === 'manual' ? (
                      <button className="btn btn-small btn-danger" onClick={() => handleRevoke(sub)} disabled={revokeMutation.isPending}>
                        Remove
                      </button>
                    ) : (
                      <span className="billing-muted">Managed in Stripe</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <p>No members with a plan yet.</p>
        </div>
      )}
    </div>
  );
};

export default BillingManagement;
